import { useEffect, useRef, useState } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  listenToChats,
  listenToMessages,
  listenToChatMeta,
  sendAdminMessage,
  setAdminTypingFlag,
  markUserMessagesSeenAndClearUnread,
} from '../utils/adminChatApi';
import { compressImageToBase64, ImageTooLargeError } from '../utils/imageCompression';
import '../styles/AdminDashboard.css';

const TYPING_TIMEOUT_MS = 2000;

function formatTime(ts) {
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function ChatListItem({ chat, isActive, onSelect }) {
  return (
    <button
      className={`admin-chat-item ${isActive ? 'admin-chat-item--active' : ''}`}
      onClick={() => onSelect(chat.id)}
    >
      <div className="admin-chat-item__top">
        <span className="admin-chat-item__name">{chat.name || 'Anonymous'}</span>
        {chat.unreadForAdmin > 0 && (
          <span className="admin-chat-item__badge">{chat.unreadForAdmin}</span>
        )}
      </div>
      <span className="admin-chat-item__subject">{chat.subject || 'No subject'}</span>
    </button>
  );
}

function AdminMessageBubble({ message, onImageClick }) {
  const isAdmin = message.sender === 'admin';
  return (
    <div className={`admin-bubble-row ${isAdmin ? 'admin-bubble-row--admin' : 'admin-bubble-row--user'}`}>
      <div className={`admin-bubble ${isAdmin ? 'admin-bubble--admin' : 'admin-bubble--user'}`}>
        {message.imageBase64 && (
          <img
            src={message.imageBase64}
            alt="Attachment"
            className="admin-bubble__image"
            onClick={() => onImageClick(message.imageBase64)}
          />
        )}
        {message.text && <p className="admin-bubble__text">{message.text}</p>}
      </div>
      <span className="admin-bubble__meta">
        {formatTime(message.createdAt)}
        {isAdmin && (message.seenByUser ? ' · Seen' : ' · Sent')}
      </span>
    </div>
  );
}

function AdminDashboard() {
  const { logout, user } = useAdminAuth();
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userTyping, setUserTypingState] = useState(false);
  const [text, setText] = useState('');
  const [pendingImage, setPendingImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => listenToChats(setChats), []);

  useEffect(() => {
    if (!selectedChatId) return undefined;
    const unsubMessages = listenToMessages(selectedChatId, setMessages);
    const unsubMeta = listenToChatMeta(selectedChatId, (data) => setUserTypingState(Boolean(data?.userTyping)));
    return () => {
      unsubMessages();
      unsubMeta();
    };
  }, [selectedChatId]);

  useEffect(() => {
    if (!selectedChatId || messages.length === 0) return;
    markUserMessagesSeenAndClearUnread(selectedChatId, messages).catch((err) =>
      console.error('Failed to mark seen:', err)
    );
  }, [selectedChatId, messages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, userTyping]);

  const handleSelectChat = (chatId) => {
    setSelectedChatId(chatId);
    setText('');
    setPendingImage(null);
    setImageError('');
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
    if (!selectedChatId) return;

    setAdminTypingFlag(selectedChatId, e.target.value.length > 0);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setAdminTypingFlag(selectedChatId, false);
    }, TYPING_TIMEOUT_MS);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImageError('');
    try {
      const base64 = await compressImageToBase64(file);
      setPendingImage(base64);
    } catch (err) {
      setImageError(
        err instanceof ImageTooLargeError ? err.message : 'Could not attach that image. Please try a different file.'
      );
    }
  };

  const handleSend = async () => {
    if (!selectedChatId || (!text.trim() && !pendingImage)) return;
    setSending(true);
    try {
      await sendAdminMessage(selectedChatId, { text: text.trim(), imageBase64: pendingImage });
      setText('');
      setPendingImage(null);
      setAdminTypingFlag(selectedChatId, false);
    } catch (err) {
      console.error('Failed to send admin message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const [previewImage, setPreviewImage] = useState(null);
  const selectedChat = chats.find((c) => c.id === selectedChatId);

  return (
    <div className="admin-dashboard">
      <aside className={`admin-sidebar ${selectedChatId ? 'admin-sidebar--hidden-mobile' : ''}`}>
        <div className="admin-sidebar__header">
          <span>Chats</span>
          <button className="admin-sidebar__logout" onClick={logout}>Sign out</button>
        </div>
        <div className="admin-sidebar__list">
          {chats.length === 0 && <p className="admin-sidebar__empty">No conversations yet</p>}
          {chats.map((chat) => (
            <ChatListItem
              key={chat.id}
              chat={chat}
              isActive={chat.id === selectedChatId}
              onSelect={handleSelectChat}
            />
          ))}
        </div>
      </aside>

      <main className={`admin-thread ${selectedChatId ? '' : 'admin-thread--hidden-mobile'}`}>
        {!selectedChatId ? (
          <div className="admin-thread__placeholder">Select a conversation</div>
        ) : (
          <>
            <div className="admin-thread__header">
              <button className="admin-thread__back" onClick={() => setSelectedChatId(null)}>←</button>
              <div>
                <p className="admin-thread__name">{selectedChat?.name || 'Anonymous'}</p>
                <p className="admin-thread__email">{selectedChat?.email}</p>
              </div>
            </div>

            <div className="admin-thread__body" ref={scrollRef}>
              {messages.map((m) => (
                <AdminMessageBubble key={m.id} message={m} onImageClick={setPreviewImage} />
              ))}
              {userTyping && (
                <div className="admin-bubble-row admin-bubble-row--user">
                  <div className="admin-bubble admin-bubble--user admin-bubble--typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
            </div>

            {imageError && <p className="admin-thread__error">{imageError}</p>}

            {pendingImage && (
              <div className="admin-thread__pending-image">
                <img src={pendingImage} alt="Pending attachment" />
                <button onClick={() => setPendingImage(null)} aria-label="Remove attachment">×</button>
              </div>
            )}

            <div className="admin-composer">
              <button className="admin-composer__attach" type="button" onClick={() => fileInputRef.current?.click()}>
                📎
              </button>
              <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} />
              <input
                className="admin-composer__input"
                placeholder="Type a reply..."
                value={text}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                onBlur={() => selectedChatId && setAdminTypingFlag(selectedChatId, false)}
              />
              <button
                className="admin-composer__send"
                onClick={handleSend}
                disabled={sending || (!text.trim() && !pendingImage)}
              >
                ↑
              </button>
            </div>
          </>
        )}
      </main>

      {previewImage && (
        <div className="admin-lightbox" onClick={() => setPreviewImage(null)}>
          <img src={previewImage} alt="Full size attachment" />
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;